/**
 * ===========================================================================
 *  ambience.js — the battlefield bed
 * ===========================================================================
 * Three continuous layers plus a sparse event scheduler:
 *
 *   wind   — pink noise through a slowly drifting bandpass, always on
 *   din    — the crowd: brown noise into three vowel-band resonators whose
 *            centres wander, so it babbles instead of hissing; swells on a
 *            0.15-0.4 Hz amplitude LFO
 *   clash  — sparse distant weapon clangs, shouts, drums and horns, scheduled
 *            against the AudioContext clock and rate-driven by intensity
 *
 * Intensity (0..1) comes from the audio system's read of the live battle:
 * bodies near the player, recent kills, mission state.
 * ===========================================================================
 */
import { gainNode, filter } from './synth.js';

export function createAmbience(graph, rnd, spawn) {
  const ctx = graph.ctx;
  const out = graph.buses.ambience.in;

  const A = {
    on: false, intensity: 0.25, target: 0.25,
    nextClash: 0, nextDrum: 0, nextHorn: 0, nextCry: 0,
    events: 0, drums: 0, horns: 0, cries: 0, clashes: 0,
  };

  const windG = gainNode(ctx, 0);
  const dinG = gainNode(ctx, 0);
  windG.connect(out); dinG.connect(out);
  const nodes = [];

  function loopSource(buf, rate) {
    const s = ctx.createBufferSource();
    s.buffer = buf; s.loop = true; s.playbackRate.value = rate;
    return s;
  }
  function lfo(freq, amount, target, base, phaseSeed) {
    const o = ctx.createOscillator();
    o.type = 'sine'; o.frequency.value = freq;
    const g = gainNode(ctx, amount);
    o.connect(g); g.connect(target);
    if (base !== undefined) target.value = base;
    o.start(ctx.currentTime + phaseSeed * 0.001);
    nodes.push(o);
    return o;
  }

  function build() {
    const t = ctx.currentTime;
    // --- wind
    const ws = loopSource(graph.noise.pink, 0.85);
    const wbp = filter(ctx, 'bandpass', 380, 0.55);
    const wlp = filter(ctx, 'lowpass', 1400, 0.7);
    ws.connect(wbp); wbp.connect(wlp); wlp.connect(windG);
    lfo(0.07, 220, wbp.frequency, 380, rnd() * 900);
    lfo(0.11, 0.09, windG.gain, 0.0, rnd() * 900);   // depth stays under the bed level: a negative gain would phase-flip the wind
    ws.start(t); nodes.push(ws);

    // --- crowd din: three wandering vowel bands over a dark noise floor
    const ds = loopSource(graph.noise.brown, 1.0);
    const ds2 = loopSource(graph.noise.pink, 0.6);
    const dlp = filter(ctx, 'lowpass', 900, 0.7);
    ds.connect(dlp); ds2.connect(dlp);
    const bands = [[520, 2.6, 0.31], [1080, 3.2, 0.20], [2350, 4.0, 0.10]];
    for (let i = 0; i < bands.length; i++) {
      const [f, q, g0] = bands[i];
      const bp = filter(ctx, 'bandpass', f, q);
      const g = gainNode(ctx, g0);
      dlp.connect(bp); bp.connect(g); g.connect(dinG);
      lfo(0.17 + i * 0.09, f * 0.22, bp.frequency, f, rnd() * 900);
      lfo(0.23 + i * 0.13, g0 * 0.6, g.gain, g0, rnd() * 900);
    }
    const dry = gainNode(ctx, 0.22); dlp.connect(dry); dry.connect(dinG);
    lfo(0.19, 0.07, dinG.gain, 0.0, rnd() * 900);
    ds.start(t); ds2.start(t); nodes.push(ds, ds2);

    // beds are mostly reverb — they are "the valley", not "a speaker"
    const sd = gainNode(ctx, 0.42); dinG.connect(sd); sd.connect(graph.verbIn);
  }

  return {
    a: A,
    start() {
      if (A.on) return;
      A.on = true; build();
      const t = ctx.currentTime;
      windG.gain.setTargetAtTime(0.30, t, 1.2);
      dinG.gain.setTargetAtTime(0.10, t, 1.5);
      A.nextClash = t + 1.2; A.nextDrum = t + 3.0; A.nextHorn = t + 14; A.nextCry = t + 2.0;
    },
    stop(fade = 1.0) {
      if (!A.on) return;
      const t = ctx.currentTime;
      windG.gain.setTargetAtTime(0, t, fade / 3);
      dinG.gain.setTargetAtTime(0, t, fade / 3);
      A.on = false;
      for (const n of nodes) { try { n.stop(t + fade); } catch { /* LFOs already stopped */ } }
      nodes.length = 0;
    },
    setIntensity(v) {
      A.target = v;
      if (!A.on) return;
      const t = ctx.currentTime;
      dinG.gain.setTargetAtTime(0.05 + v * 0.55, t, 1.4);
      windG.gain.setTargetAtTime(0.34 - v * 0.14, t, 2.0);
    },
    /** Sparse one-shots. Called once per frame; only ever schedules by clock. */
    tick(now) {
      if (!A.on) return;
      const I = A.target;
      if (now > A.nextClash) {
        // distant steel: a clang or a body hitting the dirt, hard-panned wide
        const pan = (rnd() * 2 - 1) * 0.85;
        const id = rnd() < 0.55 ? 'clang' : (rnd() < 0.5 ? 'slash' : 'bodyfall');
        spawn(id, 0.11 + rnd() * 0.13 * I, pan, 0.9);
        A.clashes++; A.events++;
        A.nextClash = now + (0.55 + rnd() * 1.6) / (0.35 + I * 1.3);
      }
      if (now > A.nextCry) {
        spawn(rnd() < 0.6 ? 'voice_battlecry' : (rnd() < 0.5 ? 'voice_death' : 'voice_shout'),
          0.08 + rnd() * 0.12 * I, (rnd() * 2 - 1) * 0.9, 1.0);
        A.cries++; A.events++;
        A.nextCry = now + (1.4 + rnd() * 3.4) / (0.3 + I * 1.2);
      }
      if (now > A.nextDrum) {
        // a distant marching pulse: two hits, a beat apart
        spawn('war_drum', 0.16 + rnd() * 0.08, (rnd() * 2 - 1) * 0.5, 1.0);
        A.drums++; A.events++;
        A.nextDrum = now + (rnd() < 0.45 ? 0.46 : 1.82 + rnd() * 2.4);
      }
      if (now > A.nextHorn) {
        spawn('war_horn', 0.16 + rnd() * 0.10, (rnd() * 2 - 1) * 0.7, 1.0);
        A.horns++; A.events++;
        A.nextHorn = now + 18 + rnd() * 26;
      }
    },
    stats() {
      return { on: A.on, intensity: +A.target.toFixed(2), wind: +windG.gain.value.toFixed(3),
        din: +dinG.gain.value.toFixed(3), events: A.events, drums: A.drums, horns: A.horns,
        cries: A.cries, clashes: A.clashes };
    },
  };
}
